import React, { useState, ChangeEvent, useEffect } from 'react';
import styled from 'styled-components';
import { Tab, Tabs, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MagicSuggestion from './MagicSuggestion/MagicSuggestion';
import Blank from './BlankActivity';
import ActivityLibrary from '../../pages/ActivityLibrary/ActivityLibrary';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { ITabProperty } from '../../pages/Settings/Settings';

const StyledTab = styled(Tab)<{ isSelected: boolean }>`
  background-color: ${({ isSelected }) => (isSelected ? 'primary.light' : 'transparent')};
  color: ${({ isSelected }) => (isSelected ? '#fff' : 'inherit')};
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 48px;
  padding: 0;
`;

const Icon = styled.img`
  height: 20px;
  width: 20px;
  margin-right: 12px;
`;

const ArrowIcon = styled.img<{ isSelected?: boolean }>`
  height: 20px;
  width: 20px;
  margin-left: 12px;
  opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
`;

const tabsProperties = [
  {
    label: 'activity.createActivity.magicSuggestion',
    tab: 'magicSuggestion',
    icon: '/assets/icons/magic-stick-2.svg',
    boldIcon: '/assets/icons/magic-stick-2-bold.svg',
    component: <MagicSuggestion />,
    permissions: [UserPermissionType.MAGIC_SUGGESTIONS_MODULE_VIEW_SUGGESTIONS],
  },
  {
    label: 'common.modules.activityLibrary',
    tab: 'activityLibrary',
    icon: '/assets/icons/activity-library-folder.svg',
    boldIcon: '/assets/icons/activity-library-folder-bold.svg',
    component: <ActivityLibrary />,
    permissions: [UserPermissionType.ACTIVITY_LIBRARY_MODULE_GET_ACTIVITY_LIBRARY],
  },
  {
    label: 'activity.createActivity.blank',
    tab: 'blank',
    icon: '/assets/icons/file.svg',
    boldIcon: '/assets/icons/file-bold.svg',
    component: <Blank />,
    permissions: [UserPermissionType.ACTIVITY_MODULE_ASSIGN_NEW_ACTIVITY],
  },
];

const TabLabel = ({
  iconSrc,
  label,
  isSelected,
}: {
  iconSrc: string;
  label: string;
  isSelected: boolean;
}) => (
  <Box
    display="flex"
    alignItems="center"
    p="16px"
    justifyContent="space-between"
    width="100%"
    bgcolor={isSelected ? 'var(--Accent-Unbleached-Silk, #FFDDC8)' : 'transparent'}
  >
    <Box display="flex" alignItems="flex-start">
      <Icon src={iconSrc} alt="notes-icon" />
      <Typography variant="bodyBold" fontWeight={isSelected ? 700 : 500}>
        {label}
      </Typography>
    </Box>
    <ArrowIcon src="/assets/icons/alt-arrow-right.svg" alt="arrow" isSelected={isSelected} />
  </Box>
);

type TabType = 0 | 1 | 2;

function CreateActivityTemplateContainer() {
  const [selectedTab, setSelectedTab] = useState<TabType>(0);
  const { t } = useTranslation();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const [filteredTabProperties, setFilteredTabProperties] = useState<ITabProperty[]>([]);

  useEffect(() => {
    const filteredTabs = tabsProperties.filter((tabProperty) =>
      checkPermissions(tabProperty.permissions, false)
    );
    setFilteredTabProperties(filteredTabs);
  }, [tabsProperties]);

  const handleChange = (event: ChangeEvent<{}>, newValue: TabType) => {
    setSelectedTab(newValue);
  };

  return (
    <Box display="flex" height="100%">
      <Box flex=".8" maxHeight="60vh" overflow="hidden">
        <Tabs
          orientation="vertical"
          value={selectedTab}
          onChange={handleChange}
          sx={{
            '.MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              minHeight: 'auto',
              width: '100%',
              maxWidth: '100%',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            },
          }}
        >
          {filteredTabProperties.map(
            (
              singleTab: {
                label: string;
                icon: string;
                boldIcon: string;
              },
              index
            ) => (
              <StyledTab
                key={singleTab.label}
                isSelected={selectedTab === index}
                label={
                  <TabLabel
                    iconSrc={selectedTab === index ? singleTab.boldIcon : singleTab.icon}
                    label={t(singleTab.label)}
                    isSelected={selectedTab === index}
                  />
                }
              />
            )
          )}
        </Tabs>
      </Box>

      {filteredTabProperties.map(
        (tabName, index) =>
          selectedTab === index && (
            <Box flex={3} height="100%" display="flex" key={tabName.label}>
              {tabName.component}
            </Box>
          )
      )}
    </Box>
  );
}

export default CreateActivityTemplateContainer;
